import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api, apiError, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EmptyState from "@/components/common/EmptyState";
import { Spinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Notifications() {
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems((l) => l.map((n) => ({ ...n, read: true })));
    await refresh();
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold text-[#1F2A2E]">Notifications</h1>
        {items.some((n) => !n.read) && (
          <Button
            variant="outline"
            onClick={markAll}
            data-testid="mark-all-read-button"
            className="bg-white border-[#E3DED4]"
          >
            <CheckCheck className="w-4 h-4 mr-1.5" /> Mark all read
          </Button>
        )}
      </div>

      <div className="mt-6">
        {loading ? (
          <Spinner label="Loading notifications" />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Bell}
            testId="notifications-empty"
            title="Nothing new yet"
            description="We will let you know when a sponsor accepts your request, when you get a message, or when a favorited sponsor opens up availability."
          />
        ) : (
          <ul data-testid="notifications-list" className="space-y-3">
            {items.map((n) => (
              <li
                key={n.id}
                data-testid={`notification-${n.id}`}
                className={`sm-card p-4 ${n.read ? "bg-white" : "bg-[#F2FAFA] border-[#BFE0E3]"}`}
              >
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full bg-[#D7EEF0] text-[#1F6F78] flex items-center justify-center shrink-0">
                    <Bell className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1F2A2E]">{n.title}</p>
                    {n.body ? <p className="mt-0.5 text-sm text-[#4B5A60] leading-6">{n.body}</p> : null}
                    <div className="mt-1.5 flex items-center gap-3">
                      <span className="text-xs text-[#7A868B]">{timeAgo(n.created_at)}</span>
                      {n.link ? (
                        <Link
                          to={n.link}
                          onClick={() => api.post(`/notifications/${n.id}/read`).then(refresh)}
                          className="text-xs font-semibold text-[#1F6F78] underline"
                        >
                          Open
                        </Link>
                      ) : null}
                    </div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#D07A4A] mt-2" />}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
